import { formatInTimeZone } from 'date-fns-tz';

import {
    isCustomOutreachRange,
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachWindowSelection,
} from '@apps/web/components/admin/outreach_observability/constants';
import { cn } from '@apps/web/lib/utils';
import { type OutreachTrendBucket } from '@libraries/typescript/api_client';

const LABEL_EVERY = 8;

// Spike-detection scaling. We don't have absolute "alarm" volumes, and the day
// is bursty (dead overnight, busy mornings), so height is relative to the
// window's own peak — but the denominator is floored at SOFT_FLOOR so a quiet
// day with a stray outlier or two stays short instead of filling the bar.
// MIN_FILLED_FRAC keeps a single-outlier bar a visible tick.
const SOFT_FLOOR = 5;
const MIN_FILLED_FRAC = 0.04;

type OutlierTickerProps = {
    readonly intraday: Array<OutreachTrendBucket>;
    readonly selectedBucketStart: string | null;
    readonly onSelect: (bucketStart: string | null) => void;
    // Yesterday's intraday buckets, time-aligned by ET time-of-day and drawn as
    // a muted background layer so today's bars can be read against the same
    // clock time a day earlier. Empty hides the ghost layer and its legend.
    readonly ghostBuckets?: Array<OutreachTrendBucket>;
    // The active live window. The yesterday ghost is only meaningful for
    // 'today': the 'trailing_24h' window already includes yesterday's bars, so a
    // ghost would compare those buckets against themselves.
    readonly window: OutreachWindowSelection;
    // Width of each bucket, used for its tooltip and accessible range label.
    readonly bucketMs: number;
};

export function formatOutlierBucketRange(
    startMs: number,
    bucketMs: number,
    window: OutreachWindowSelection,
): string {
    const endMs = startMs + bucketMs;
    const timeFormat = 'h:mm a';
    // Custom ranges can sit anywhere in the past, so their bucket labels
    // always carry the date; presets only need it once they span days.
    const includeDate =
        isCustomOutreachRange(window) ||
        window === 'trailing_3d' ||
        window === 'trailing_7d' ||
        window === 'trailing_14d';
    const startFormat = includeDate ? `MMM d, ${timeFormat}` : timeFormat;
    const sameEtDay =
        formatInTimeZone(
            new Date(startMs),
            OUTREACH_ANALYTICS_TIMEZONE,
            'yyyy-MM-dd',
        ) ===
        formatInTimeZone(
            new Date(endMs),
            OUTREACH_ANALYTICS_TIMEZONE,
            'yyyy-MM-dd',
        );
    const endFormat = includeDate && !sameEtDay ? startFormat : timeFormat;
    return `${formatInTimeZone(
        new Date(startMs),
        OUTREACH_ANALYTICS_TIMEZONE,
        startFormat,
    )}–${formatInTimeZone(
        new Date(endMs),
        OUTREACH_ANALYTICS_TIMEZONE,
        endFormat,
    )}`;
}

function formatHour(ms: number): string {
    return new Date(ms)
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            timeZone: OUTREACH_ANALYTICS_TIMEZONE,
        })
        .toLowerCase();
}

// Minutes since ET midnight, the alignment key so a today bucket lines up with
// the yesterday bucket at the same clock time regardless of calendar date.
function etMinutesOfDay(iso: string): number {
    const parts = formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'H:m',
    ).split(':');
    return Number(parts[0]) * 60 + Number(parts[1]);
}

export function OutlierTicker({
    intraday,
    selectedBucketStart,
    onSelect,
    ghostBuckets = [],
    window,
    bucketMs,
}: OutlierTickerProps) {
    const peak = intraday.reduce(
        (max, bucket) => Math.max(max, bucket.outlier_count),
        0,
    );
    const denominator = Math.max(peak, SOFT_FLOOR);
    const lastIndex = intraday.length - 1;
    // Only overlay yesterday when the window is 'today'; in 'trailing_24h' half
    // the bars already are yesterday, so a ghost would double them onto
    // themselves.
    const hasGhost = ghostBuckets.length > 0 && window === 'today';
    // Ghosts share today's scale (same denominator) so equal counts read equal;
    // a bigger yesterday just clamps at full height and stays faint, keeping
    // today's solid bars dominant.
    const ghostByMinute = new Map(
        ghostBuckets.map((bucket) => [
            etMinutesOfDay(bucket.bucket_start),
            bucket.outlier_count,
        ]),
    );

    return (
        <div className="flex flex-col gap-1">
            {hasGhost ? (
                <div className="text-muted-foreground flex items-center gap-3 text-[10px]">
                    <span className="flex items-center gap-1">
                        <span className="inline-block h-2 w-2 rounded-[1px] bg-amber-500" />
                        today
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="bg-muted-foreground/25 inline-block h-2 w-2 rounded-[1px]" />
                        yesterday
                    </span>
                </div>
            ) : null}
            <div className="flex h-32 items-end gap-[2px]">
                {intraday.map((bucket) => {
                    const ghostCount =
                        ghostByMinute.get(
                            etMinutesOfDay(bucket.bucket_start),
                        ) ?? 0;
                    const ghostPct =
                        ghostCount === 0
                            ? 0
                            : Math.min(1, ghostCount / denominator) * 100;
                    const ghostLayer = hasGhost ? (
                        <div
                            aria-hidden="true"
                            style={{ height: `${ghostPct}%` }}
                            className="bg-muted-foreground/20 absolute inset-x-0 bottom-0 rounded-[1px]"
                        />
                    ) : null;
                    if (bucket.outlier_count === 0) {
                        return (
                            <div
                                key={bucket.bucket_start}
                                className="relative flex h-full flex-1 items-end"
                            >
                                {ghostLayer}
                                <div className="bg-muted/40 relative h-0.5 w-full rounded-[1px]" />
                            </div>
                        );
                    }
                    const heightPct =
                        Math.max(
                            MIN_FILLED_FRAC,
                            bucket.outlier_count / denominator,
                        ) * 100;
                    const isSelected =
                        bucket.bucket_start === selectedBucketStart;
                    const startMs = Date.parse(bucket.bucket_start);
                    const range = formatOutlierBucketRange(
                        startMs,
                        bucketMs,
                        window,
                    );
                    const noun =
                        bucket.outlier_count === 1 ? 'outlier' : 'outliers';
                    return (
                        <div
                            key={bucket.bucket_start}
                            className="relative flex h-full flex-1 items-end"
                        >
                            {ghostLayer}
                            <button
                                type="button"
                                style={{ height: `${heightPct}%` }}
                                title={`${range} · ${bucket.outlier_count} ${noun}`}
                                aria-label={`${range}, ${bucket.outlier_count} ${noun}`}
                                onClick={() => {
                                    onSelect(
                                        isSelected ? null : bucket.bucket_start,
                                    );
                                }}
                                className={cn(
                                    'relative w-full rounded-[2px] transition-opacity hover:opacity-80',
                                    bucket.destructive_outlier_count > 0
                                        ? 'bg-red-500'
                                        : 'bg-amber-500',
                                    isSelected &&
                                        'ring-foreground/70 ring-2 ring-offset-1',
                                )}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="text-muted-foreground/60 flex gap-[2px] text-[10px] tabular-nums">
                {intraday.map((bucket, index) => (
                    <span
                        key={bucket.bucket_start}
                        className="flex-1 overflow-visible text-center whitespace-nowrap"
                    >
                        {index === lastIndex
                            ? 'now'
                            : index % LABEL_EVERY === 0
                              ? formatHour(Date.parse(bucket.bucket_start))
                              : ''}
                    </span>
                ))}
            </div>
        </div>
    );
}
